import { InputControl } from '@/components/input-control';
import { JSONControl } from '@/components/json-control';
import { MediaPlaceholder } from '@/components/media-placeholder';
import { SettingsPanel } from '@/components/settings-panel';
import { SwitchControl } from '@/components/switch-control';
import { LocalLinkSelector } from '@/js/shared/components/ui/LocalLinkSelector';
import { SettingsContainer } from '@/node-editor/components';
import {
  getNodeOverride,
  hasNodeOverride,
} from '@/node-editor/store/selectors';
import { INode, INodeProps } from '@/types/node';
import React, { FC, Fragment, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IContent } from './contract';
import { ButtonGroupControl } from '@/components/buttongroup-control';
import { SelectControl } from '@/components/select-control';
import { ManagerMarkdownEditor } from '@/components/markdown/managerMarkdownEditor';
import { SETTINGS_DEFAULTS } from '@/node-editor/store';
import {
  FormControlLabel,
  Grid,
  Icon,
  Switch,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}
interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}
export const Settings = ({
  node: {
    id,
    type,
    override,
    content: {
      imageHeight = 0,
      src,
      link,
      text,
      alignment,
      verticalSpacing,
      postTitle,
      dateTime,
    },
  },
  conferenceId,
}: IProps) => {
  const { watch, setValue, control, getValues } = useFormContext();
  const isHeightAuto = (watch('imageHeight') ?? imageHeight) === 0;

  const watchTargetUrl = watch('link.targetUrl', link.targetUrl);
  const watchTarget = watch('link.target', link.target);

  const { VERTICAL_SPACE_OPTIONS, ALIGNMENT_OPTIONS } = SETTINGS_DEFAULTS;

  const parseDateTime = (dt: string | undefined): Date => {
    const date = dt ? new Date(dt) : new Date();
    return !isNaN(date.getTime()) ? date : new Date();
  };
  const combineDateTime = (date: Date, time: Date): Date => {
    const d = new Date(date);
    d.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    return d;
  };
  const getDateTime = (value: string | undefined) =>
    parseDateTime(value || dateTime);

  useEffect(() => {
    if (!getValues('dateTime'))
      setValue('dateTime', parseDateTime(dateTime).toISOString(), {
        shouldDirty: false,
      });
  }, []);

  useEffect(() => {
    setValue(
      'link.targetRel',
      watchTarget === '_blank' ? 'noreferrer' : undefined
    );
  }, [watchTarget, setValue]);

  return (
    <Fragment>
      <SettingsPanel label="Image Settings" initialOpen>
        <Controller
          name="src"
          defaultValue={src}
          render={({ field: { onChange, value } }) => (
            <MediaPlaceholder
              label=""
              value={value}
              onChange={onChange}
              conferenceId={conferenceId}
            />
          )}
        />

        <Grid container alignItems="center">
          <Grid size={{ sm: 3 }}>
            <FormControlLabel
              label={`${isHeightAuto ? 'Auto' : 'Min'} height`}
              control={
                <Switch
                  checked={isHeightAuto}
                  onChange={(_e, checked) => {
                    setValue(
                      'imageHeight',
                      checked ? 0 : (watch('imageHeight') ?? imageHeight) || 200
                    );
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#78AF36',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#78AF36 !important',
                    },
                  }}
                />
              }
            />
          </Grid>
          {!isHeightAuto && (
            <Grid size={{ sm: 9 }}>
              <InputControl
                fullWidth
                name="imageHeight"
                label="Height settings"
                type="number"
                defaultValue={imageHeight}
                variant="outlined"
                style={{ marginBottom: 0 }}
              />
            </Grid>
          )}
        </Grid>
        <Typography paragraph variant="caption">
          Select image height : either Auto height or give a specific height
          value.
        </Typography>

        <InputControl
          name="link.targetUrl"
          defaultValue={link.targetUrl}
          fullWidth
          variant="outlined"
          label="Image URL"
        />
        <div>
          <LocalLinkSelector
            userType="manager"
            conferenceId={conferenceId}
            ButtonProps={{ color: 'primary' }}
            dataCallback={(linkUrl) => setValue('link.targetUrl', linkUrl)}
          />
        </div>
        {watchTargetUrl && (
          <SwitchControl
            name="link.target"
            label="Open link in new tab?"
            defaultValue={link.target}
            options={['_blank', '_self']}
          />
        )}
      </SettingsPanel>

      <SettingsPanel label="Title and Dates" initialOpen>
        <InputControl
          name="postTitle"
          label="Post Heading"
          defaultValue={postTitle}
          variant="outlined"
          fullWidth
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="dateTime"
                control={control}
                render={({ field }) => {
                  const dt = getDateTime(field.value);
                  return (
                    <DatePicker
                      label="Date"
                      value={dt}
                      format="dd/MM/yyyy"
                      onChange={(date: Date | null) =>
                        date &&
                        field.onChange(combineDateTime(date, dt).toISOString())
                      }
                      slotProps={{
                        textField: { variant: 'outlined', fullWidth: true },
                      }}
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="dateTime"
                control={control}
                render={({ field }) => {
                  const dt = getDateTime(field.value);
                  return (
                    <TimePicker
                      label="Time"
                      value={dt}
                      ampm={false}
                      onChange={(time: Date | null) =>
                        time &&
                        field.onChange(combineDateTime(dt, time).toISOString())
                      }
                      format="HH:mm"
                      timeSteps={{ minutes: 1 }}
                      slotProps={{
                        textField: { variant: 'outlined', fullWidth: true },
                      }}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </SettingsPanel>

      <SettingsPanel label="Text Content" initialOpen>
        <Controller
          control={control}
          name="text"
          defaultValue={text}
          render={({ field: { onChange, value } }) => (
            <ManagerMarkdownEditor
              value={value}
              setValue={onChange}
              conferenceId={conferenceId}
            />
          )}
        />
      </SettingsPanel>
      <SettingsPanel label="Alignment Settings" initialOpen>
        <Grid container spacing={4}>
          <Grid>
            <ButtonGroupControl
              label="Horizontal alignment"
              name="alignment.horizontal"
              defaultValue={alignment.horizontal}
              options={[
                {
                  label: 'Left',
                  value: ALIGNMENT_OPTIONS.start,
                  component: <Icon>format_align_left</Icon>,
                },
                {
                  label: 'Center',
                  value: ALIGNMENT_OPTIONS.center,
                  component: <Icon>format_align_justify</Icon>,
                },
                {
                  label: 'Right',
                  value: ALIGNMENT_OPTIONS.end,
                  component: <Icon>format_align_right</Icon>,
                },
              ]}
            />
          </Grid>
        </Grid>
      </SettingsPanel>
      <SettingsPanel label="Vertical Space Settings" initialOpen>
        <Grid container spacing={2}>
          <Grid size={{ sm: 6 }}>
            <SelectControl
              label="Top"
              name="verticalSpacing.top"
              defaultValue={verticalSpacing.top}
              options={VERTICAL_SPACE_OPTIONS}
            />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <SelectControl
              label="Bottom"
              name="verticalSpacing.bottom"
              defaultValue={verticalSpacing.bottom}
              options={VERTICAL_SPACE_OPTIONS}
            />
          </Grid>
        </Grid>
      </SettingsPanel>
      <SettingsPanel
        label="Css override"
        initialOpen={useMemo(() => hasNodeOverride(type, override), [])}
      >
        <JSONControl
          id={id}
          name="override"
          defaultValue={getNodeOverride(type, override)}
        />
      </SettingsPanel>
    </Fragment>
  );
};

export const Edit: FC<IProps> = ({ children, ...props }) => (
  <SettingsContainer>
    <Settings {...props} />
  </SettingsContainer>
);
